import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import JSZip from "jszip";
import { api, itemImage, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { FolderArchive, FolderOpen, Images, Sparkles, Check, X, Loader2, Clock, ArrowRight, RotateCcw, Layers, AlertTriangle, UploadCloud } from "lucide-react";
import { toast } from "sonner";

const JOB_KEY = "wardrobe.bulkJobId";
const ORDER = { processing: 0, queued: 1, failed: 2, done: 3 };
const IMAGE_RE = /\.(jpe?g|png|webp|heic|heif|bmp|gif|tiff?)$/i;
const BATCH_SIZE = 4;          // photos per request
const BATCH_BYTES = 6 * 1024 * 1024; // keep each request well under proxy limits
const MAX_DIM = 1600;

/** Shrink an image in the browser to a JPEG <= MAX_DIM. Falls back to the original (e.g. HEIC). */
const compressImage = async (file) => {
  if (!/\.(jpe?g|png|webp|bmp|gif)$/i.test(file.name)) return file; // HEIC etc -> server converts
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, MAX_DIM / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale), h = Math.round(bmp.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
    bmp.close?.();
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.86));
    if (!blob) return file;
    const name = file.name.replace(/\.[^.]+$/, ".jpg");
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    return file;
  }
};

/** Expand a FileList / drop into a flat list of image Files (unzipping ZIPs in the browser). */
const collectImages = async (fileList) => {
  const out = [];
  for (const f of Array.from(fileList || [])) {
    const rel = f.webkitRelativePath || f.name;
    if (/\.zip$/i.test(f.name)) {
      const zip = await JSZip.loadAsync(f);
      for (const entry of Object.values(zip.files)) {
        if (entry.dir || !IMAGE_RE.test(entry.name) || entry.name.includes("__MACOSX") || /(^|\/)\./.test(entry.name)) continue;
        const blob = await entry.async("blob");
        const base = entry.name.split("/").pop();
        out.push({ file: new File([blob], base), path: entry.name });
      }
    } else if (IMAGE_RE.test(f.name) && !f.name.startsWith(".")) {
      out.push({ file: f, path: rel });
    }
  }
  // natural sort by path so IMG_0009 comes before IMG_0010
  const key = (p) => p.split(/(\d+)/).map((t) => (/^\d+$/.test(t) ? t.padStart(8, "0") : t.toLowerCase())).join("");
  out.sort((a, b) => key(a.path).localeCompare(key(b.path)));
  return out.slice(0, 800);
};

export const BulkImport = () => {
  const navigate = useNavigate();
  const folderRef = useRef(null);
  const photosRef = useRef(null);
  const [photos, setPhotos] = useState([]);       // [{file, path}]
  const [enhance, setEnhance] = useState(true);
  const [upload, setUpload] = useState(null);     // {sent, total}
  const [job, setJob] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [collecting, setCollecting] = useState(false);

  // resume an in-flight job after refresh / navigation (saved id first, else any running import)
  useEffect(() => {
    const saved = localStorage.getItem(JOB_KEY);
    const fallback = () => api.bulkActive().then((j) => { if (j) { localStorage.setItem(JOB_KEY, j.id); setJob(j); } }).catch(() => {});
    if (!saved) { fallback(); return; }
    api.bulkStatus(saved).then((j) => {
      if (j.status !== "completed" && j.status !== "uploading") setJob(j);
      else { localStorage.removeItem(JOB_KEY); fallback(); }
    }).catch(() => { localStorage.removeItem(JOB_KEY); fallback(); });
  }, []);

  // poll job status
  useEffect(() => {
    if (!job || job.status === "completed") return;
    const t = setInterval(async () => {
      try {
        const j = await api.bulkStatus(job.id);
        setJob(j);
        if (j.status === "completed") {
          clearInterval(t);
          localStorage.removeItem(JOB_KEY);
          toast.success(`Imported ${j.done} of ${j.total} items`);
        }
      } catch (e) { /* keep polling */ }
    }, 1500);
    return () => clearInterval(t);
  }, [job?.id, job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setCollecting(true);
    try {
      const imgs = await collectImages(fileList);
      if (imgs.length === 0) { toast.error("No photos found — pick a folder, photos, or a .zip of images"); return; }
      setPhotos((prev) => {
        const seen = new Set(prev.map((p) => p.path));
        return [...prev, ...imgs.filter((p) => !seen.has(p.path))].slice(0, 800);
      });
    } catch (e) {
      toast.error("Could not read those files");
    } finally {
      setCollecting(false);
    }
  };

  const onDrop = async (e) => {
    e.preventDefault(); setDragging(false);
    // support dropping a folder via DataTransferItem.webkitGetAsEntry
    const items = Array.from(e.dataTransfer.items || []);
    const entries = items.map((i) => i.webkitGetAsEntry?.()).filter(Boolean);
    if (entries.some((en) => en.isDirectory)) {
      const files = [];
      const walk = (entry, prefix) => new Promise((resolve) => {
        if (entry.isFile) {
          entry.file((f) => { files.push(Object.assign(f, { _path: prefix + f.name })); resolve(); }, () => resolve());
        } else if (entry.isDirectory) {
          const reader = entry.createReader();
          const readAll = (acc) => reader.readEntries(async (batch) => {
            if (!batch.length) { for (const en of acc) await walk(en, prefix + entry.name + "/"); resolve(); }
            else readAll(acc.concat(batch));
          }, () => resolve());
          readAll([]);
        } else resolve();
      });
      for (const en of entries) await walk(en, "");
      // emulate webkitRelativePath
      files.forEach((f) => { try { Object.defineProperty(f, "webkitRelativePath", { value: f._path }); } catch { /* ignore */ } });
      await pick(files);
    } else {
      await pick(e.dataTransfer.files);
    }
  };

  const start = async () => {
    if (photos.length === 0) return;
    setUpload({ sent: 0, total: photos.length });
    let jobId = null;
    try {
      const j = await api.bulkStart(enhance, photos.length);
      jobId = j.id;
      // compress + upload in small batches
      let batch = [], bytes = 0, sent = 0;
      const flush = async () => {
        if (!batch.length) return;
        await api.bulkAddPhotos(jobId, batch);
        sent += batch.length;
        setUpload({ sent, total: photos.length });
        batch = []; bytes = 0;
      };
      for (const p of photos) {
        const f = await compressImage(p.file);
        // keep the folder-relative path so ordering/grouping matches how you shot them
        const named = new File([f], p.path.replace(/\//g, "__"), { type: f.type });
        if (batch.length >= BATCH_SIZE || bytes + named.size > BATCH_BYTES) await flush();
        batch.push(named); bytes += named.size;
      }
      await flush();
      const started = await api.bulkFinish(jobId);
      localStorage.setItem(JOB_KEY, jobId);
      setJob(started);
      setPhotos([]);
      toast.info(`${photos.length} photos uploaded — sorting them into items now`);
    } catch (e) {
      const msg = e?.response?.data?.detail || (e?.response?.status === 413 ? "A batch was too large — try again" : "Upload failed — check your connection and try again");
      toast.error(msg);
    } finally {
      setUpload(null);
    }
  };
  const reset = () => { setJob(null); setPhotos([]); localStorage.removeItem(JOB_KEY); };

  const regroup = async () => {
    try {
      const j = await api.bulkRegroup(job.id);
      localStorage.setItem(JOB_KEY, j.id);
      setJob(j);
      toast.info("Looking for front / back / tag sequences…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not merge");
    }
  };

  const retry = async () => {
    try {
      const j = await api.bulkRetry(job.id);
      localStorage.setItem(JOB_KEY, j.id);
      setJob(j);
      toast.info("Retrying the failed items…");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not retry");
    }
  };

  if (job) {
    const grouping = job.status === "grouping";
    const pct = job.total ? Math.round(((job.done + job.failed) / job.total) * 100) : 0;
    const finished = job.status === "completed";
    const remaining = job.total - job.done - job.failed;
    const perItem = job.seconds_per_item || 22;
    const conc = job.concurrency || 5;
    const etaSec = Math.ceil(remaining / conc) * perItem;
    const eta = etaSec >= 60 ? `~${Math.ceil(etaSec / 60)} min left` : `~${etaSec}s left`;
    const sorted = [...(job.entries || [])].sort((a, b) => (ORDER[a.status] - ORDER[b.status]) || (a.index - b.index));
    const canMerge = finished && !job.regroup_of && job.entries.filter((e) => e.status === "done").length > 1
      && !job.entries.some((e) => e.has_back || e.has_tag);
    return (
      <div className="rounded-[var(--radius)] border border-border bg-card p-5" data-testid="bulk-import-progress">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-xl">
              {finished ? (job.regroup_of ? "Merge complete" : "Import complete") : grouping ? "Sorting your photos…" : job.regroup_of ? "Merging duplicates…" : "Importing your closet…"}
            </h3>
            {grouping ? (
              <p className="text-sm text-muted-foreground mt-1" data-testid="bulk-import-summary">
                Looking at {job.photo_count} photos to pair each front with its back and tag.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-1" data-testid="bulk-import-summary">
                {job.done} {job.regroup_of ? "merged" : "added"} · {job.failed} failed · {remaining} remaining{!finished && remaining > 0 && <span> · {eta}</span>}
              </p>
            )}
            {!finished && !grouping && (
              <p className="text-xs text-muted-foreground mt-1">
                {job.enhance ? `Analyzing and de-wrinkling ${conc} items at a time — about ${perItem}s each.` : `Analyzing ${conc} items at a time.`} You can leave this page; we'll keep going.
              </p>
            )}
            {finished && job.error && <p className="text-xs text-destructive mt-1">{job.error}</p>}
            {finished && job.entries.some((e) => e.possible_duplicate) && (
              <p className="text-xs mt-1 flex items-center gap-1.5 text-action" data-testid="bulk-import-duplicate-note">
                <AlertTriangle className="h-3.5 w-3.5" /> {job.entries.filter((e) => e.possible_duplicate).length} flagged as possible duplicates — review them in your closet.
              </p>
            )}
          </div>
          {!finished && <Loader2 className="h-5 w-5 animate-spin text-action shrink-0" />}
        </div>
        <Progress value={grouping ? 3 : pct} className="mt-4" data-testid="bulk-import-progress-bar" />
        {finished && job.done > 0 && (
          <div className="mt-4 rounded-2xl bg-foreground text-background p-4 flex flex-wrap items-center justify-between gap-3" data-testid="bulk-import-final-pass-cta">
            <div>
              <p className="font-display text-lg">Now the final pass</p>
              <p className="text-xs text-background/70 mt-0.5">Rate how likely you are to wear each piece, sort keep / sell, confirm colours, catch dirty ones and duplicates - then list the sell pile in one tap.</p>
            </div>
            <Button onClick={() => navigate("/review")} className="rounded-full bg-background text-foreground hover:bg-background/90" data-testid="bulk-import-start-review">Start final pass <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
          </div>
        )}

        <ul className="mt-5 divide-y divide-border max-h-[420px] overflow-y-auto -mx-2" data-testid="bulk-import-list">
          {sorted.map((e) => (
            <li key={e.index} className="flex items-center gap-3 px-2 py-2.5" data-testid={`bulk-entry-${e.index}`}>
              <div className="h-12 w-12 rounded-lg bg-secondary overflow-hidden shrink-0 flex items-center justify-center">
                {e.status === "done" && e.item_id ? (
                  <img src={itemImage({ id: e.item_id, has_enhanced: e.has_enhanced })} alt={e.name || e.key} className="h-full w-full object-cover" />
                ) : e.status === "processing" ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : e.status === "failed" ? (
                  <X className="h-4 w-4 text-destructive" />
                ) : (
                  <Clock className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{e.name || e.key}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {e.status === "failed" ? e.error : e.files.join(", ")}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {e.has_tag && <Badge variant="outline" className="text-[10px]">tag</Badge>}
                {e.has_back && <Badge variant="outline" className="text-[10px]">back</Badge>}
                {e.extras > 0 && <Badge variant="outline" className="text-[10px]">+{e.extras} detail</Badge>}
                {e.status === "done" && e.possible_duplicate && <Badge className="text-[10px] bg-action text-action-foreground hover:bg-action" data-testid={`bulk-entry-duplicate-${e.index}`}><AlertTriangle className="h-3 w-3 mr-1" /> Possible duplicate</Badge>}
                {e.status === "done" && e.retail_price != null && <span className="text-[11px] text-muted-foreground font-mono-plex">new {money(e.retail_price)}</span>}
                {e.status === "done" && <Badge variant="secondary" className="capitalize">{e.category}</Badge>}
                {e.status === "done" && <Check className="h-4 w-4 text-action" />}
              </div>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-3 mt-5">
          <Button onClick={() => navigate("/inventory")} className="rounded-full flex-1" data-testid="bulk-import-view-closet">
            View closet <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
          {canMerge && (
            <Button variant="outline" onClick={regroup} className="rounded-full" data-testid="bulk-import-merge-button">
              <Layers className="h-4 w-4 mr-1.5" /> Merge front / back / tag
            </Button>
          )}
          {finished && job.failed > 0 && (
            <Button variant="outline" onClick={retry} className="rounded-full" data-testid="bulk-import-retry-button">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Retry {job.failed} failed
            </Button>
          )}
          {finished && (
            <Button variant="secondary" onClick={reset} className="rounded-full" data-testid="bulk-import-another">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Import another
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[var(--radius)] border border-border bg-card p-5" data-testid="bulk-import-panel">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center">
          <FolderOpen className="h-5 w-5 text-action" />
        </div>
        <div>
          <h3 className="font-display text-xl">Import a whole batch</h3>
          <p className="text-sm text-muted-foreground">Pick a folder, a bunch of photos, or a ZIP. We shrink and upload them in small chunks so big batches don't fail.</p>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`mt-4 rounded-[var(--radius)] border-2 border-dashed p-6 text-center transition-colors ${dragging ? "border-action bg-secondary/60" : "border-border"}`}
        data-testid="bulk-import-dropzone"
      >
        <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden"
               onChange={(e) => { pick(e.target.files); e.target.value = ""; }} data-testid="bulk-import-folder-input" />
        <input ref={photosRef} type="file" accept="image/*,.heic,.heif,.zip,application/zip" multiple className="hidden"
               onChange={(e) => { pick(e.target.files); e.target.value = ""; }} data-testid="bulk-import-file-input" />
        {collecting ? (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Reading files…</div>
        ) : (
          <>
            <UploadCloud className="h-7 w-7 mx-auto text-muted-foreground" />
            <p className="text-sm font-medium mt-2">Drop a folder or photos here</p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
              <Button type="button" variant="secondary" size="sm" className="rounded-full" onClick={() => folderRef.current?.click()} data-testid="bulk-import-pick-folder">
                <FolderOpen className="h-4 w-4 mr-1.5" /> Choose folder
              </Button>
              <Button type="button" variant="secondary" size="sm" className="rounded-full" onClick={() => photosRef.current?.click()} data-testid="bulk-import-pick-photos">
                <Images className="h-4 w-4 mr-1.5" /> Choose photos or ZIP
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">JPG, PNG, WEBP or HEIC. Up to 800 photos.</p>
          </>
        )}
      </div>

      {photos.length > 0 && (
        <div className="mt-4 rounded-[var(--radius)] border border-border p-3" data-testid="bulk-import-selection">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{photos.length} photo{photos.length === 1 ? "" : "s"} ready</p>
            <button onClick={() => setPhotos([])} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="bulk-import-clear"><X className="h-3.5 w-3.5" /> Clear</button>
          </div>
          <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
            {photos.slice(0, 24).map((p) => (
              <Thumb key={p.path} file={p.file} />
            ))}
            {photos.length > 24 && <div className="h-14 w-14 shrink-0 rounded-lg bg-secondary flex items-center justify-center text-xs text-muted-foreground">+{photos.length - 24}</div>}
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-3">
        Shoot each garment as <span className="font-medium text-foreground">front → back → tag</span> in a row and we'll pair them into one item automatically. Or name files <code className="font-mono bg-secondary px-1 rounded">shirt_front.jpg</code> / <code className="font-mono bg-secondary px-1 rounded">shirt_back.jpg</code> / <code className="font-mono bg-secondary px-1 rounded">shirt_tag.jpg</code>.
      </p>

      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          <Sparkles className="h-4 w-4 text-action" />
          <span>AI de-wrinkle & lay flat every photo</span>
        </div>
        <Switch checked={enhance} onCheckedChange={setEnhance} data-testid="bulk-import-enhance-toggle" />
      </div>

      {upload && (
        <div className="mt-4" data-testid="bulk-import-upload-progress">
          <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
            <span>Uploading photos…</span><span>{upload.sent} / {upload.total}</span>
          </div>
          <Progress value={upload.total ? (upload.sent / upload.total) * 100 : 0} />
        </div>
      )}

      <Button onClick={start} disabled={photos.length === 0 || !!upload} className="mt-4 w-full rounded-full" data-testid="bulk-import-start-button">
        {upload ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderArchive className="h-4 w-4 mr-2" />}
        {upload ? `Uploading ${upload.sent}/${upload.total}…` : photos.length ? `Import ${photos.length} photo${photos.length === 1 ? "" : "s"}` : "Import"}
      </Button>
    </div>
  );
};

const Thumb = ({ file }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!/\.(jpe?g|png|webp|gif|bmp)$/i.test(file.name)) return undefined;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return (
    <div className="h-14 w-14 shrink-0 rounded-lg bg-secondary overflow-hidden flex items-center justify-center" title={file.name}>
      {url ? <img src={url} alt={file.name} className="h-full w-full object-cover" /> : <Images className="h-4 w-4 text-muted-foreground" />}
    </div>
  );
};
